/**
 * OrdersCardList Section Component
 *
 * Mobile replacement for OrdersTable — renders each order as a stacked card
 * (Figma: Grain Core Design 14789:14286). Shown below the `md` breakpoint by
 * AllOrdersPage; the table remains the desktop rendering.
 */

import { memo, useEffect, useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { Bar } from '../../../Skeleton/primitives';
import { ChevronIcon, EmptySkuIcon } from '../../../icons';
import { MobileIconButton } from '../../../layouts/MobileButtons';
import { StatusBadge } from '@/components/ui/status-badge';
import { ORDER_STATUS_BADGE_CONFIG } from '../../../OrderDetails/order-status-config';
import {
  XERO_INVOICE_BADGE_CONFIG,
  xeroInvoiceState,
} from '../../../OrderDetails/xero-invoice-config';
import { UserCell } from '../../../UserCell';
import { formatCurrency } from '../../../../utils/formatters';
import type { Order } from '@api/orders';
import type { OrdersCardListProps } from '../types';

export type { OrdersCardListProps };

const SKELETON_CARD_COUNT = 3;

interface OrderCardProps {
  order: Order;
  onOrderIdClick?: (orderId: number) => void;
}

export const OrderCard = memo(function OrderCard({ order, onOrderIdClick }: OrderCardProps) {
  const contact = order.contact;
  const contactDetails = contact
    ? [contact.full_name, contact.phone, contact.organisation_name].filter(Boolean).join(' · ')
    : '';
  const events = order.events ?? [];
  // Card lists up to two events (tag + date); any beyond that collapse into a
  // "View more" affordance — tapping the card opens the full list in the detail
  // sheet (Figma 14769:95809).
  const visibleEvents = events.slice(0, 2);
  const hasMoreEvents = events.length > visibleEvents.length;
  const handleOpen = onOrderIdClick ? () => onOrderIdClick(order.id) : undefined;
  const cardClassName = cn(
    'flex w-full flex-col gap-4 rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter p-4 text-left',
    handleOpen &&
      'cursor-pointer touch-manipulation hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30',
  );

  const handleCardKeyDown = (e: KeyboardEvent) => {
    if (!handleOpen) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const cardBody = (
    <>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-inter text-sm leading-normal font-semibold text-off-black whitespace-nowrap">
            {order.reference_number}
          </span>
          {/* Keep badges inert because the card is the tap target. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <StatusBadge value={order.order_status} config={ORDER_STATUS_BADGE_CONFIG} />
            <StatusBadge
              value={xeroInvoiceState(order.xero_invoice_id)}
              config={XERO_INVOICE_BADGE_CONFIG}
            />
          </div>
        </div>
        {contact ? (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate font-inter text-xs leading-tight-sm font-semibold text-off-black">
              {contact.email}
            </span>
            {contactDetails && (
              <span className="truncate font-inter text-xs leading-tight-sm text-off-black">
                {contactDetails}
              </span>
            )}
          </div>
        ) : (
          <span className="font-inter text-xs leading-tight-sm text-grey">-</span>
        )}
      </div>

      <div className="h-px w-full bg-dinnerplate-white" />

      {events.length === 0 ? (
        <span className="font-inter text-xs leading-tight-sm text-grey">-</span>
      ) : (
        <div className="flex w-full flex-col gap-2">
          {visibleEvents.map((event) => (
            <div key={event.event_id} className="flex w-full items-center gap-2 overflow-hidden">
              <span className="shrink-0 rounded-xs border border-off-black bg-off-white px-2 py-1 font-inter text-xs font-normal leading-tight-sm text-off-black">
                {event.event_id}
              </span>
              <span className="truncate font-inter text-xs font-normal leading-tight-sm text-off-black">
                {event.date}
              </span>
            </div>
          ))}
          {hasMoreEvents && (
            <span className="font-inter text-xs font-normal leading-tight-sm text-grey">
              View more
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <UserCell user={order.salesperson} />
        <span className="whitespace-nowrap font-inter text-xs font-semibold leading-tight-sm text-off-black tabular-nums">
          {formatCurrency(order.total_amount)}
        </span>
      </div>
    </>
  );

  if (handleOpen) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={cardClassName}
        onClick={handleOpen}
        onKeyDown={handleCardKeyDown}
      >
        {cardBody}
      </div>
    );
  }

  return <div className={cardClassName}>{cardBody}</div>;
});

function SkeletonCard() {
  return (
    <div className="flex w-full flex-col gap-4 rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter p-4">
      <div className="flex items-center justify-between gap-2">
        <Bar className="h-4 w-32 rounded" />
        <Bar className="h-6 w-24 rounded" />
      </div>
      <Bar className="h-4 w-3/4 rounded" />
      <div className="h-px w-full bg-dinnerplate-white" />
      <div className="flex items-center gap-3">
        <Bar className="size-12 rounded-full" />
        <Bar className="h-4 w-24 rounded" />
      </div>
    </div>
  );
}

export function OrdersCardList({
  orders,
  isLoading = false,
  isFetching = false,
  onOrderIdClick,
  pagination,
  translations,
  className,
}: OrdersCardListProps) {
  const emptyLabel = translations?.no_results ?? 'No orders found';
  const showSkeleton = isLoading && orders.length === 0;

  // The list is its own scroll container, so a page change would otherwise
  // leave the user at the bottom (where the pager sits) of the new page.
  const scrollRef = useRef<HTMLDivElement>(null);
  const page = pagination?.page;
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <div
      ref={scrollRef}
      className={cn(
        'flex flex-col gap-3 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]',
        isFetching && !showSkeleton && 'opacity-70 transition-opacity duration-200 ease-out motion-reduce:transition-none',
        className
      )}
    >
      {showSkeleton ? (
        Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => <SkeletonCard key={index} />)
      ) : orders.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center py-16">
          <EmptySkuIcon className="mb-4 w-16 h-16 text-grey" />
          <p className="font-inter text-sm text-grey">{emptyLabel}</p>
        </div>
      ) : (
        orders.map((order) => (
          <OrderCard key={order.id} order={order} onOrderIdClick={onOrderIdClick} />
        ))
      )}
      {!showSkeleton && orders.length > 0 && pagination && pagination.totalPages > 1 && (
        <nav className="flex shrink-0 items-center justify-between pt-1" aria-label="Pagination">
          <MobileIconButton
            aria-label="Go to previous page"
            disabled={pagination.page <= 1}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
            className="disabled:opacity-50"
          >
            <ChevronIcon direction="left" />
          </MobileIconButton>
          <span className="font-inter text-sm leading-normal text-grey tabular-nums">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <MobileIconButton
            aria-label="Go to next page"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
            className="disabled:opacity-50"
          >
            <ChevronIcon direction="right" />
          </MobileIconButton>
        </nav>
      )}
    </div>
  );
}
